import React from 'react';
import {SegmentedButton, Size, TextArea, TextInput} from "@dts-soldel/dts-react-common";
import {observer} from "mobx-react";
import ICON_TYPES from "../../../../../../../src/js/misc/IconTypes";
import PropTypes from "prop-types";
import HelpIcon from "../../HelpIcon/HelpIcon";
import HelpText from "../../HelpIcon/HelpText";
import startCase from "lodash/startCase";

const propTypes = {
	altText: PropTypes.string,
	domValue: PropTypes.string,
	functionValue: PropTypes.string,
	iconType: PropTypes.string,
	label: PropTypes.string.isRequired,
	onAltTextChange: PropTypes.func,
	onDomValueChange: PropTypes.func.isRequired,
	onFunctionValueChange: PropTypes.func.isRequired,
	onStringValueChange: PropTypes.func.isRequired,
	onTypeChange: PropTypes.func.isRequired,
	onUrlValueChange: PropTypes.func.isRequired,
	popOverTextKey: PropTypes.string.isRequired,
	stringValue: PropTypes.string,
	urlValue: PropTypes.string,
};
const defaultProps = {
	altText: '',
	domValue: '',
	functionValue: '',
	iconType: '',

	// if defined, alt text entry is shown; logo header menus have alt text but main menu does not
	onAltTextChange: undefined,
	stringValue: '',
	urlValue: '',
};

const fieldSetup = ({iconType, domValue, functionValue, stringValue, urlValue, onDomValueChange, onFunctionValueChange, onStringValueChange, onUrlValueChange}) => {
	let textFieldName;
	let textFieldValue;
	let textFieldOnChange;

	switch (iconType) {
		case ICON_TYPES.DOMNODE:
			textFieldName = `dom`;
			textFieldValue = domValue || '';
			textFieldOnChange = onDomValueChange;
			break;

		case ICON_TYPES.FUNCTION:
			textFieldName = `function`;
			textFieldValue = functionValue || '';
			textFieldOnChange = onFunctionValueChange;
			break;

		case ICON_TYPES.STRING:
			textFieldName = `string`;
			textFieldValue = stringValue || '';
			textFieldOnChange = onStringValueChange;
			break;

		case ICON_TYPES.URL:
			textFieldName = `url`;
			textFieldValue = urlValue || '';
			textFieldOnChange = onUrlValueChange;
			break;

		default:
			iconType && console.error(`IconSimpleInput.fieldSetup(): Unknown header logo type: ${iconType}`);
			break;
	}
	return {
		textFieldName,
		textFieldValue,
		textFieldOnChange: (field, value) => textFieldOnChange(value),
	}
};

const IconSimpleInput = ({
	altText,
	domValue,
	functionValue,
	iconType,
	label,
	onTypeChange,
	popOverTextKey,
	stringValue,
	urlValue,
	onAltTextChange,
	onDomValueChange,
	onFunctionValueChange,
	onStringValueChange,
	onUrlValueChange
}) => {

	const {textFieldName, textFieldValue, textFieldOnChange} = fieldSetup({
		iconType,
		domValue, functionValue, stringValue, urlValue,
		onDomValueChange, onFunctionValueChange, onStringValueChange, onUrlValueChange
	});

	return (
		<div className="configurator__inputs__input segmented-button-group__background">
			<SegmentedButton
				field="iconType"
				label={`${label} Type`}
				className="size-full"
				options={[
					{label: 'Off', value: ''},
					{label: 'DOM', value: ICON_TYPES.DOMNODE},
					{label: 'Func', value: ICON_TYPES.FUNCTION},
					{label: 'Str', value: ICON_TYPES.STRING},
					{label: 'URL', value: ICON_TYPES.URL},
				]}
				value={iconType || ''}
				onChange={(field, value) => onTypeChange(value)}
				children={
					<HelpIcon
						onUseExampleValue={onTypeChange}
						popOverText={HelpText[popOverTextKey]}
					/>
				}
			/>
			{
				iconType ?
					<TextArea
						size={Size.FULL_WIDTH}
						field={textFieldName}
						label={label}
						value={textFieldValue}
						onChange={textFieldOnChange}
						children={
							<HelpIcon
								popOverText={HelpText[`${popOverTextKey}${startCase(textFieldName)}`]}
								onUseExampleField={HelpText[`${popOverTextKey}${startCase(textFieldName)}`].exampleValue ? textFieldName : null}
								onUseExampleFieldValue={HelpText[`${popOverTextKey}${startCase(textFieldName)}`].exampleValue ? textFieldOnChange : null}
							/>
						}
					/>
					: undefined
			}
			{
				(iconType && onAltTextChange) ?
					<TextInput
						size={Size.FULL_WIDTH}
						field="altText"
						label="Alt Text"
						value={altText}
						onChange={(field, value) => onAltTextChange(value)}
						children={
							<HelpIcon
								popOverText={HelpText[`${popOverTextKey}AltText`]}
								onUseExampleField={HelpText[`${popOverTextKey}AltText`].exampleValue ? textFieldName : null}
								onUseExampleFieldValue={HelpText[`${popOverTextKey}AltText`].exampleValue ? (field, value) => onAltTextChange(value) : null}
							/>
						}
					/>
					: undefined
			}
		</div>
	);
};

IconSimpleInput.propTypes = propTypes;
IconSimpleInput.defaultProps = defaultProps;

export default observer(IconSimpleInput);
